"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { FiPrinter } from "react-icons/fi";
import { HiOutlinePaperAirplane } from "react-icons/hi";
import { Button } from "@/components/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getPrintableNoteParts } from "@/lib/note-utils";
import { cx } from "@/lib/tailwind-utils";
import { NoteDetail } from "@/types/notes";
export type NoteExportDestination = "print" | "fax";
interface PrintNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteDetail: NoteDetail;
  /**
   * How many photos are attached to this note. The photo option only appears
   * when there is at least one.
   */
  photoCount?: number;
  /**
   * Called with the question IDs to omit from the printout, the export options
   * chosen in the dialog, and where the document is headed — the printer or a
   * fax. Both send the same document; only the destination differs.
   */
  onConfirm: (options: {
    excludedQuestionIds: Set<string>;
    hideMedications: boolean;
    hideMedicationDosage: boolean;
    hideMedicalInformation: boolean;
    includePhotos: boolean;
    destination: NoteExportDestination;
  }) => void | Promise<void>;
  isPrinting?: boolean;
  /**
   * Whether to offer "Fax" beside "Print". False when the user can't send
   * faxes or the clinic has no fax line, in which case the button is hidden
   * rather than shown disabled — an option that can never work is noise.
   */
  canFax?: boolean;
  /** True while the note is being turned into a fax (rendered and uploaded). */
  isFaxing?: boolean;
}

/**
 * Lets a provider choose which parts of a note to include when printing /
 * exporting the copy given to a patient. Defaults to the template's Patient
 * View selection (`patientVisibleQuestionOrder`); when that isn't configured,
 * every printable part is pre-selected. Selection is per-print and not saved.
 */
export default function PrintNoteDialog(t0) {
  const $ = _c(92);
  const {
    open,
    onOpenChange,
    noteDetail,
    photoCount: t1,
    onConfirm,
    isPrinting: t2,
    canFax: t3,
    isFaxing: t4
  } = t0;
  const photoCount = t1 === undefined ? 0 : t1;
  const isPrinting = t2 === undefined ? false : t2;
  const canFax = t3 === undefined ? false : t3;
  const isFaxing = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== noteDetail) {
    t5 = getPrintableNoteParts(noteDetail);
    $[0] = noteDetail;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const groups = t5;
  let t6;
  if ($[2] !== groups) {
    t6 = groups.flatMap(_temp2);
    $[2] = groups;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const allIds = t6;
  let t7;
  if ($[4] !== allIds || $[5] !== noteDetail.template?.patientVisibleQuestionOrder) {
    t7 = () => {
      const order = noteDetail.template?.patientVisibleQuestionOrder;
      if (Array.isArray(order)) {
        const allowed = new Set(allIds);
        return new Set(order.filter(id => allowed.has(id)));
      }
      return new Set(allIds);
    };
    $[4] = allIds;
    $[5] = noteDetail.template?.patientVisibleQuestionOrder;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  noteDetail.template?.patientVisibleQuestionOrder;
  let t8;
  if ($[7] !== t7) {
    t8 = t7();
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const defaultIncluded = t8;
  const [included, setIncluded] = useState(defaultIncluded);
  const [hideMedications, setHideMedications] = useState(false);
  const [hideMedicationDosage, setHideMedicationDosage] = useState(false);
  const [hideMedicalInformation, setHideMedicalInformation] = useState(false);
  const [includePhotos, setIncludePhotos] = useState(false);
  let t10;
  let t9;
  if ($[9] !== defaultIncluded || $[10] !== open) {
    t9 = () => {
      if (open) {
        setIncluded(new Set(defaultIncluded));
        setHideMedications(false);
        setHideMedicationDosage(false);
        setHideMedicalInformation(false);
        setIncludePhotos(false);
      }
    };
    t10 = [open, defaultIncluded];
    $[9] = defaultIncluded;
    $[10] = open;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  useEffect(t9, t10);
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = questionId => {
      setIncluded(prev => {
        const next = new Set(prev);
        if (next.has(questionId)) {
          next.delete(questionId);
        } else {
          next.add(questionId);
        }
        return next;
      });
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const togglePart = t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = questionIds => {
      setIncluded(prev_0 => {
        const next_0 = new Set(prev_0);
        const allOn = questionIds.every(id_0 => next_0.has(id_0));
        for (const id_1 of questionIds) {
          if (allOn) {
            next_0.delete(id_1);
          } else {
            next_0.add(id_1);
          }
        }
        return next_0;
      });
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const toggleSection = t12;
  const allSelected = allIds.length > 0 && included.size === allIds.length;
  const nothingSelected = allIds.length > 0 && included.size === 0;
  const isBusy = isPrinting || isFaxing;
  let t13;
  if ($[15] !== allIds) {
    t13 = on => setIncluded(on ? new Set(allIds) : new Set());
    $[15] = allIds;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const setAll = t13;
  let t14;
  if ($[17] !== allIds || $[18] !== hideMedicalInformation || $[19] !== hideMedicationDosage || $[20] !== hideMedications || $[21] !== includePhotos || $[22] !== included || $[23] !== onConfirm) {
    t14 = destination => {
      const excluded = new Set(allIds.filter(id_2 => !included.has(id_2)));
      onConfirm({
        excludedQuestionIds: excluded,
        hideMedications,
        hideMedicationDosage,
        hideMedicalInformation,
        includePhotos,
        destination
      });
    };
    $[17] = allIds;
    $[18] = hideMedicalInformation;
    $[19] = hideMedicationDosage;
    $[20] = hideMedications;
    $[21] = includePhotos;
    $[22] = included;
    $[23] = onConfirm;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const handleConfirm = t14;
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DialogHeader><DialogTitle>Select what to include</DialogTitle><DialogDescription>Uncheck any parts you don't want in the copy that leaves the clinic. This affects this printout or fax only.</DialogDescription></DialogHeader>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = checked => setHideMedications(checked === true);
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== hideMedications) {
    t17 = <Checkbox checked={hideMedications} onCheckedChange={t16} />;
    $[27] = hideMedications;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="space-y-0.5"><span className="block text-body-strong text-foreground">Hide prescribed medications</span><span className="block text-caption text-foreground-muted">Leave out the Prescribed Medications section entirely — no medication names are printed. Use this for a requisition or referral that shouldn't carry the patient's medication list.</span></span>;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17) {
    t19 = <label className="flex items-start gap-2 rounded-md border border-border bg-background-secondary p-3">{t17}{t18}</label>;
    $[30] = t17;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = hideMedications && "opacity-50";
  let t21;
  if ($[32] !== t20) {
    t21 = cx("flex items-start gap-2 rounded-md border border-border bg-background-secondary p-3", t20);
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = checked_0 => setHideMedicationDosage(checked_0 === true);
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== hideMedicationDosage || $[36] !== hideMedications) {
    t23 = <Checkbox checked={hideMedicationDosage} disabled={hideMedications} onCheckedChange={t22} />;
    $[35] = hideMedicationDosage;
    $[36] = hideMedications;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="space-y-0.5"><span className="block text-body-strong text-foreground">Hide medication dosage</span><span className="block text-caption text-foreground-muted">Show medication names only — dose, strength, and frequency are left out of the printed copy.</span></span>;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t21 || $[40] !== t23) {
    t25 = <label className={t21}>{t23}{t24}</label>;
    $[39] = t21;
    $[40] = t23;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = checked_1 => setHideMedicalInformation(checked_1 === true);
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== hideMedicalInformation) {
    t27 = <Checkbox checked={hideMedicalInformation} onCheckedChange={t26} />;
    $[43] = hideMedicalInformation;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="space-y-0.5"><span className="block text-body-strong text-foreground">Hide medical information</span><span className="block text-caption text-foreground-muted">Leave out the Medical Information section — patient-reported medications and health history.</span></span>;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t27) {
    t29 = <label className="flex items-start gap-2 rounded-md border border-border bg-background-secondary p-3">{t27}{t28}</label>;
    $[46] = t27;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== includePhotos || $[49] !== photoCount) {
    t30 = photoCount > 0 && <label className="flex items-start gap-2 rounded-md border border-border bg-background-secondary p-3"><Checkbox checked={includePhotos} onCheckedChange={checked_2 => setIncludePhotos(checked_2 === true)} /><span className="space-y-0.5"><span className="block text-body-strong text-foreground">Include photos ({photoCount})</span><span className="block text-caption text-foreground-muted">Add the photos attached to this note, with any markings the provider drew. Check this for a copy kept in the chart or sent for an audit.</span></span></label>;
    $[48] = includePhotos;
    $[49] = photoCount;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== allIds.length || $[52] !== allSelected || $[53] !== groups || $[54] !== included || $[55] !== setAll) {
    t31 = allIds.length === 0 ? <p className="text-body text-foreground-muted">This note has no selectable fields. Patient demographics will still be included, along with medications unless hidden above.</p> : <div className="space-y-4"><button type="button" onClick={() => setAll(!allSelected)} className="text-caption font-medium text-primary hover:underline">{allSelected ? "Deselect all" : "Select all"}</button>{groups.map((group, groupIdx) => {
        if (group.sectionId) {
          const ids = group.parts.map(_temp3);
          const onCount = ids.filter(id_3 => included.has(id_3)).length;
          const sectionChecked = onCount === 0 ? false : onCount === ids.length ? true : "indeterminate";
          return <div key={group.sectionId ?? `section-${groupIdx}`} className="space-y-2"><label className="flex items-center gap-2"><Checkbox checked={sectionChecked} onCheckedChange={() => toggleSection(ids)} /><span className="text-body-strong text-foreground">{group.sectionName || "Section"}</span></label><div className="space-y-2 pl-6">{group.parts.map(part => <label key={part.questionId} className="flex items-center gap-2"><Checkbox checked={included.has(part.questionId)} onCheckedChange={() => togglePart(part.questionId)} /><span className="text-body text-foreground-secondary">{part.label}</span></label>)}</div></div>;
        }
        const part_0 = group.parts[0];
        return <label key={part_0.questionId} className="flex items-center gap-2"><Checkbox checked={included.has(part_0.questionId)} onCheckedChange={() => togglePart(part_0.questionId)} /><span className="text-body text-foreground">{part_0.label}</span></label>;
      })}</div>;
    $[51] = allIds.length;
    $[52] = allSelected;
    $[53] = groups;
    $[54] = included;
    $[55] = setAll;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t19 || $[58] !== t25 || $[59] !== t29 || $[60] !== t30 || $[61] !== t31) {
    t32 = <DialogBody className="max-h-[60vh] space-y-4 overflow-y-auto">{t19}{t25}{t29}{t30}{t31}</DialogBody>;
    $[57] = t19;
    $[58] = t25;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== onOpenChange) {
    t33 = () => onOpenChange(false);
    $[63] = onOpenChange;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== isBusy || $[66] !== t33) {
    t34 = <Button variant="secondary" onClick={t33} disabled={isBusy}>Cancel</Button>;
    $[65] = isBusy;
    $[66] = t33;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== canFax || $[69] !== handleConfirm || $[70] !== isFaxing || $[71] !== isPrinting || $[72] !== nothingSelected) {
    t35 = canFax && <Button variant="secondary" onClick={() => handleConfirm("fax")} isLoading={isFaxing} loadingText="Preparing..." disabled={nothingSelected || isPrinting} icon={<HiOutlinePaperAirplane className="size-4" />}>Fax</Button>;
    $[68] = canFax;
    $[69] = handleConfirm;
    $[70] = isFaxing;
    $[71] = isPrinting;
    $[72] = nothingSelected;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== handleConfirm) {
    t36 = () => handleConfirm("print");
    $[74] = handleConfirm;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  const t37 = nothingSelected || isFaxing;
  let t38;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <FiPrinter className="size-4" />;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== isPrinting || $[78] !== t36 || $[79] !== t37) {
    t39 = <Button onClick={t36} isLoading={isPrinting} loadingText="Preparing..." disabled={t37} icon={t38}>Print</Button>;
    $[77] = isPrinting;
    $[78] = t36;
    $[79] = t37;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t34 || $[82] !== t35 || $[83] !== t39) {
    t40 = <DialogFooter>{t34}{t35}{t39}</DialogFooter>;
    $[81] = t34;
    $[82] = t35;
    $[83] = t39;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t32 || $[86] !== t40) {
    t41 = <DialogContent className="max-w-lg">{t15}{t32}{t40}</DialogContent>;
    $[85] = t32;
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== onOpenChange || $[89] !== open || $[90] !== t41) {
    t42 = <Dialog open={open} onOpenChange={onOpenChange}>{t41}</Dialog>;
    $[88] = onOpenChange;
    $[89] = open;
    $[90] = t41;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  return t42;
}
function _temp3(p_0) {
  return p_0.questionId;
}
function _temp2(g) {
  return g.parts.map(_temp);
}
function _temp(p) {
  return p.questionId;
}